import { useState } from "react";
import { Eye, EyeOff, Plus, Shield, Trash2, Zap } from "lucide-react";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import { id, tx } from "@instantdb/react";
import { Input, Label } from "../components/ui/index.ts";

export const AdminSpyBots = () => {
  const [phone, setPhone] = useState("");
  const [token, setToken] = useState("");
  const [isPassive, setIsPassive] = useState(false);
  const [showTokens, setShowTokens] = useState<Record<string, boolean>>({});
  const [error, setError] = useState("");

  const botsQuery = db.useQuery({
    spyBots: {},
  });

  if (botsQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (botsQuery.error) {
    return <div className="text-red-400 p-4">{botsQuery.error.message}</div>;
  }

  const { spyBots } = botsQuery.data;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const cleanPhone = phone.trim().replace(/\D/g, "");
    if (!cleanPhone) {
      setError("Please enter a valid phone number");
      return;
    }

    if (!token.trim()) {
      setError("Please enter a Supergreen API token");
      return;
    }

    // Check if phone already exists
    if (spyBots.some((b) => b.phone.replace(/\D/g, "") === cleanPhone)) {
      setError("A spy bot with this phone number already exists.");
      return;
    }

    db.transact(
      tx.spyBots[id()].update({
        phone: cleanPhone,
        token: token.trim(),
        isPassive,
      }),
    ).then(() => {
      setPhone("");
      setToken("");
      setIsPassive(false);
    }).catch((err) => {
      setError(`Failed to save: ${err.message}`);
    });
  };

  const handleTogglePassive = (botId: string, currentPassive: boolean) => {
    db.transact(
      tx.spyBots[botId].update({
        isPassive: !currentPassive,
      }),
    );
  };

  const handleDelete = (botId: string, botPhone: string) => {
    if (
      confirm(`Are you sure you want to delete the spy bot for ${botPhone}?`)
    ) {
      db.transact(tx.spyBots[botId].delete());
    }
  };

  const toggleShowToken = (botId: string) => {
    setShowTokens((prev) => ({
      ...prev,
      [botId]: !prev[botId],
    }));
  };

  return (
    <div className="space-y-8 p-4">
      {/* Form Card */}
      <div className="bg-gray-900 border border-white/10 rounded-xl p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Plus size={20} className="text-purple-500" />
          Add New Spy Bot
        </h3>

        <form onSubmit={handleAdd} className="space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-500/50 text-red-200 text-sm p-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="mb-2">
                Phone Number
              </Label>
              <Input
                type="text"
                placeholder="e.g. 972509918446"
                value={phone}
                onChange={(e) =>
                  setPhone((e.target as unknown as { value: string }).value)}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Numbers only, including country code (no leading + or 0s)
              </p>
            </div>

            <div>
              <Label className="mb-2">
                Supergreen API Token
              </Label>
              <Input
                type="password"
                placeholder="Enter token"
                value={token}
                onChange={(e) =>
                  setToken((e.target as unknown as { value: string }).value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 bg-black/30 border border-white/5 rounded-lg p-3">
            <input
              type="checkbox"
              id="isPassive"
              checked={isPassive}
              onChange={(e) =>
                setIsPassive(
                  (e.target as unknown as { checked: boolean }).checked,
                )}
              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 bg-black/50"
            />
            <label
              htmlFor="isPassive"
              className="text-sm text-gray-300 cursor-pointer select-none"
            >
              <span className="font-medium text-white block">Passive Bot</span>
              <span className="text-xs text-gray-500 block">
                If enabled, the bot will only listen to group messages and will
                NOT reply to DMs or auto-join groups from DMs.
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-2.5 rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Add Spy Bot
          </button>
        </form>
      </div>

      {/* List Card */}
      <div className="bg-gray-900 border border-white/10 rounded-xl p-6 shadow-2xl">
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center justify-between">
          <span>Configured Spy Bots</span>
          <span className="text-sm font-normal text-gray-500">
            {spyBots.length} active bot{spyBots.length === 1 ? "" : "s"}
          </span>
        </h3>

        {spyBots.length === 0
          ? (
            <div className="text-center text-gray-500 py-12 border border-dashed border-white/10 rounded-lg">
              No spy bots configured in the database yet. The system is
              currently falling back to Infisical environment variables.
            </div>
          )
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {spyBots.map((bot) => {
                const isBotPassive = !!bot.isPassive;
                const isShowing = !!showTokens[bot.id];

                return (
                  <div
                    key={bot.id}
                    className="bg-black/40 border border-white/10 rounded-lg p-5 flex flex-col justify-between hover:border-white/20 transition-all space-y-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-lg font-bold text-white flex items-center gap-2">
                          +{bot.phone}
                        </div>
                        <div className="mt-1 flex items-center gap-1.5">
                          {isBotPassive
                            ? (
                              <span className="inline-flex items-center gap-1 bg-yellow-500/10 text-yellow-500 text-xs px-2.5 py-0.5 rounded-full font-medium border border-yellow-500/20">
                                <Shield size={12} />
                                Passive (Webhook Listening Only)
                              </span>
                            )
                            : (
                              <span className="inline-flex items-center gap-1 bg-green-500/10 text-green-500 text-xs px-2.5 py-0.5 rounded-full font-medium border border-green-500/20">
                                <Zap size={12} />
                                Active (Auto-Joins & Replies)
                              </span>
                            )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDelete(bot.id, bot.phone)}
                        className="text-gray-500 hover:text-red-400 p-1.5 hover:bg-red-500/10 rounded-lg transition"
                        title="Delete spy bot"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="space-y-2 bg-black/50 border border-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">
                          Supergreen API Token
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleShowToken(bot.id)}
                          className="text-gray-400 hover:text-white transition flex items-center gap-1 text-xs"
                        >
                          {isShowing
                            ? (
                              <>
                                <EyeOff size={12} />
                                Hide
                              </>
                            )
                            : (
                              <>
                                <Eye size={12} />
                                Show
                              </>
                            )}
                        </button>
                      </div>
                      <div className="font-mono text-xs text-gray-300 break-all select-all">
                        {isShowing
                          ? bot.token
                          : "••••••••••••••••••••••••••••••••"}
                      </div>
                    </div>

                    <div className="pt-2 border-t border-white/5 flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Change Mode:
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handleTogglePassive(bot.id, isBotPassive)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition border flex items-center gap-1.5 ${
                          isBotPassive
                            ? "bg-purple-600/20 border-purple-500/30 text-purple-400 hover:bg-purple-600/30"
                            : "bg-yellow-600/10 border-yellow-500/20 text-yellow-400 hover:bg-yellow-600/20"
                        }`}
                      >
                        {isBotPassive
                          ? (
                            <>
                              <Zap size={12} />
                              Make Active
                            </>
                          )
                          : (
                            <>
                              <Shield size={12} />
                              Make Passive
                            </>
                          )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
};
